import { AuthenticationService } from 'src/app/service/authentication.service';
import { ProxySettings } from './../../model/Setting';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { GeneralService } from 'src/app/service/general.service';
import { Events } from 'src/app/service/websocket.service';
import { EventService } from 'src/app/service/event.service';
import { finalize, tap } from 'rxjs/operators';
import { Modal } from '../controls/modal/modal.service';
import { LoginProxyComponent, ProxyModalConfig, ProxyModalResult } from '../dialogs/login-proxy/login-proxy.component';
import { Utils } from 'src/app/utils';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { UserInfo } from 'src/app/model/User';
import { emptyValidator } from 'src/app/validator/empty-form-validator';
import { ModalConfig } from '../controls/modal/modal-config';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-settings-proxy',
    templateUrl: './settings-proxy.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-proxy.component.css'],
    standalone: false
})
export class SettingsProxyComponent implements OnInit {

  isLoading:boolean = false;
  isSaving:boolean = false;
  showPwd:boolean = false;

  settingsForm = new FormGroup({
    isProxyModeCtrl: new FormControl(),
    serverUrlCtrl: new FormControl('', [
      Validators.required,
      Validators.maxLength(50),
      emptyValidator()
    ]),
    portCtrl: new FormControl('', [
      Validators.max(999999),
      Validators.pattern("^[0-9]*$")
    ]),
    authTypeCtrl: new FormControl('', [Validators.required, noNtlmValidator()]),
    storeCredentialsCtrl: new FormControl(),
    usernameCtrl: new FormControl(),
    passwordCtrl: new FormControl(),
    exceptionURLCtrl: new FormControl(),
  });
  savingBtn:string = $localize`Save`;
  currentUserAuthInfo : UserInfo;
  settings: ProxySettings = new ProxySettings();
  exceptionURLList:string [] = [];

  constructor(private genService: GeneralService,
    private eventService: EventService,
    private authService: AuthenticationService,
    private modal: Modal) { }

  get isProxyModeCtrl() { return this.settingsForm.get('isProxyModeCtrl'); }
  get serverUrlCtrl() { return this.settingsForm.get('serverUrlCtrl'); }
  get portCtrl() { return this.settingsForm.get('portCtrl'); }
  get authTypeCtrl() { return this.settingsForm.get('authTypeCtrl'); }
  get storeCredentialsCtrl() { return this.settingsForm.get('storeCredentialsCtrl'); }
  get usernameCtrl() { return this.settingsForm.get('usernameCtrl'); }
  get passwordCtrl() { return this.settingsForm.get('passwordCtrl'); }
  get exceptionURLCtrl() { return this.settingsForm.get('exceptionURLCtrl'); }

  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.initData();
  }

  initData(showLoading:boolean = true): void {
    if(showLoading){
      this.isLoading = true;
    }
    this.genService.getProxySettings().pipe(tap(resp => {
      this.storeCredentialsCtrl.valueChanges.subscribe(value => {
        this.setStoreCredentialsCtrlValidator(value);
      });
      this.isProxyModeCtrl.valueChanges.subscribe(value => {
        //when turning off proxy server and store credential was checked
        if(!value && this.storeCredentialsCtrl.value){
          //uncheck store credential
          this.storeCredentialsCtrl.setValue(value);
        }
      });
      if(resp.body != null){
        this.settingsForm.reset(this.settingsForm.value);
        this.settings = resp.body;
        this.isProxyModeCtrl.setValue(this.settings.isProxyMode);
        this.serverUrlCtrl.setValue(this.settings.serverUrl);
        this.portCtrl.setValue(this.settings.port);
        this.authTypeCtrl.setValue(this.settings.authType);
        this.storeCredentialsCtrl.setValue(this.settings.storeCredentials);
        this.usernameCtrl.setValue(this.settings.username);

        if(this.settings.nonProxyServerUrls != ''){
          this.exceptionURLList = this.settings.nonProxyServerUrls.split('|');
        }
      }

      Object.values(this.settingsForm.controls).forEach(control => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });
    }),
    finalize(() => {
      this.isLoading = false;
      this.setFormControlsEnabled();
    }),
    ).subscribe();
  }

  save(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Save`,
          message: $localize`The changes made on this page will be saved. Do you want to save?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-proxy-save'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;
          this.savingBtn = $localize`Saving...`;

          if (this.authService.getLoggedInUser().settings.isOnlineMode &&
            this.isProxyModeCtrl.value && !this.storeCredentialsCtrl.value &&
            this.authTypeCtrl.value != 'none') {
            const sub: Subscription =
              this.modal.open(LoginProxyComponent, {
                data: new ProxyModalConfig(false),
                id: 'otsc-proxy-login-dialog',
                maxheight: '800px'
              }
              ).afterClosed.subscribe((result: ProxyModalResult) => {
                if (result.ok) {
                  this.submitChanges(result.user, result.password);
                }
                sub.unsubscribe();
              });
          } else {
            this.submitChanges(this.usernameCtrl.value, this.passwordCtrl.value);
          }
        }
        sub.unsubscribe();
      });
  }

  submitChanges(username:string, password:string): void {
    this.settings.isProxyMode = this.isProxyModeCtrl.value;
    this.settings.serverUrl = this.serverUrlCtrl.value;
    this.settings.port = this.portCtrl.value;
    this.settings.nonProxyServerUrls = this.exceptionURLList.join('|');
    this.settings.storeCredentials = this.storeCredentialsCtrl.value;
    this.settings.authType = this.authTypeCtrl.value;
    this.settings.username = this.usernameCtrl.value;
    this.settings.password = this.passwordCtrl.value;

    this.genService.updateProxySettings(this.settings, username, password).pipe(tap(resp => {
      if(resp.body){
        Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully`);
        this.currentUserAuthInfo.settings.proxyPrompt = this.settings.isProxyMode && !this.settings.storeCredentials && this.settings.authType !== 'none';
        this.authService.updateLoggedInUser(this.currentUserAuthInfo);
        this.initData(false);
      }else{
        Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Settings could not be saved. Please try again later.`);
        this.settingsForm.reset(this.settingsForm.value);
      }
    }),
    finalize(() => {
      this.isSaving = false;
      this.savingBtn = $localize`Save`;
    }),
    ).subscribe();
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to reset?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-proxy-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.initData();
        }
        sub.unsubscribe();
      });
  }

  setStoreCredentialsCtrlValidator(setValidator:boolean): void {
    if(setValidator && this.isProxyModeCtrl.value){
      this.usernameCtrl.setValidators([Validators.required, emptyValidator()]);
    }else{
      this.usernameCtrl.clearValidators();
    }

    if(setValidator && this.isProxyModeCtrl.value){
      this.passwordCtrl.setValidators([Validators.required]);
    }else{
      this.passwordCtrl.clearValidators();
    }

    this.usernameCtrl.updateValueAndValidity();
    this.passwordCtrl.updateValueAndValidity();
  }

  addToExceptions(): void {
    if(this.exceptionURLList.indexOf(this.exceptionURLCtrl.value) > -1){
      this.modal.open(ModalMessageComponent, {
        data: {
                title: $localize`Error`,
                message: $localize`'${this.exceptionURLCtrl.value}' is already added to the exceptions.`,
                primaryBtn: $localize`OK`,
                type: ModalMessageType.Error,
                width: '350px',
              },
        id: 'otsc-settings-proxy-invalid-exception'
        }
      );
    }else if(this.exceptionURLCtrl.value && this.exceptionURLCtrl.value.trim() !== ''){
      this.exceptionURLList.push(this.exceptionURLCtrl.value);
      this.exceptionURLCtrl.setValue('');
    }
  }

  canDeactivate() : boolean{
    return !this.settingsForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-proxy-unsaved-changes');
  }

  setFormControlsEnabled(): void {
    if(!this.isProxyModeCtrl.value){
      this.authTypeCtrl.disable();
    } else {
      this.authTypeCtrl.enable();
    }
    if(!this.isProxyModeCtrl.value || this.authTypeCtrl.value == 'none'){
      this.storeCredentialsCtrl.disable();
    } else {
      this.storeCredentialsCtrl.enable();
    }
  }
}


// Custom validator to prevent 'ntlm' selection
export function noNtlmValidator(): ValidatorFn {
  return (control: AbstractControl) => {
    return control.value === 'ntlm' ? { ntlmNotAllowed: true } : null;
  };
}